import type { ComponentType, PropsWithChildren } from 'react';
import React, { createRef, useCallback } from 'react';
import { styled } from '@compiled/react';

import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import { ContentTitleWithByLine } from '@confluence/view-page-common';
import { ContentContainer } from '@confluence/content-container';
import { DeepLinkHandler } from '@confluence/deep-link-handler';
import { ScrollToHashLinkHandler } from '@confluence/scroll';
import { Attribution } from '@confluence/error-boundary';
import { gridSize } from '@confluence/typography-placeholder';
import { PageLoadEnd } from '@confluence/browser-metrics';
import { useDialogs } from '@confluence/dialogs/entry-points/useDialogs';
import { fg } from '@confluence/feature-gating';

import { LazyLearnMoreLozenge } from './LazyLearnMoreLozenge';
import { UserTypes } from './constants';
import { PUBLIC_LINK_SHARED_PAGE_LOAD } from './perf.config';
import { LearnMoreModal } from './learnMoreModal/LearnMoreModal';

export type ContentRenderer = ComponentType<{
	contentId: string | null;
	scrollTo: string;
	isMobileView: boolean;
}>;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FabricContainer = styled.div({
	width: '100%',
	margin: '0 auto',
	minHeight: '80vh',
	display: 'flex',
	flexDirection: 'column',
	justifyContent: 'space-between',
});

const childrenContainerStyles = xcss({
	marginTop: 'space.200',
});

type ViewPageExternalProps = PropsWithChildren<{
	contentId: string;
	queryHash?: string;
	isMobileView: boolean;
	ContentRenderer: ContentRenderer;
	spaceKey: string;
	userType: UserTypes;
	cloudId: string;
	publicLinkId: string;
}>;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TitlePadding = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${gridSize * 4}px`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const MobileContentContainer = styled.div({
	position: 'relative',
	background: token('elevation.surface', '#fff'),
	paddingTop: 0,
	paddingRight: token('space.200', '16px'),
	paddingBottom: token('space.250', '20px'),
	paddingLeft: token('space.200', '16px'),
	minHeight: '100vh',
	boxSizing: 'border-box',
});

const Container = <
	T extends PropsWithChildren<{
		contentId: string;
		isMobileView: boolean;
	}>,
>(
	props: T,
) => {
	return props.isMobileView ? (
		<MobileContentContainer data-testid="view-page-external-mobile-content-container">
			{props.children}
		</MobileContentContainer>
	) : (
		<ContentContainer
			contentId={props.contentId}
			isScreenContentView
			isContainerContentView
			errorAttribution={Attribution.EXTERNAL_COLLAB}
		>
			{props.children}
		</ContentContainer>
	);
};

export const ViewPageExternal = ({
	contentId,
	queryHash = '',
	ContentRenderer,
	isMobileView,
	children,
	spaceKey,
	userType,
	cloudId,
	publicLinkId,
}: ViewPageExternalProps) => {
	const refOfNodeToWatchForChange = createRef<HTMLDivElement>();
	const isAnonymousUser = userType === UserTypes.ANONYMOUS;
	const isContentIdRemovalEnabled = fg('public_links_content_id_removal');

	const { showModal } = useDialogs();
	const launchPLLearnMoreModal = useCallback(
		(openTrigger = 'click') => {
			showModal(LearnMoreModal, {
				contentId,
				cloudId,
				userType,
				openTrigger,
			});
		},
		[showModal, contentId, cloudId, userType],
	);

	return (
		<Container isMobileView={isMobileView} contentId={contentId}>
			<TitlePadding />
			<ContentTitleWithByLine
				contentId={contentId}
				spaceKey={spaceKey}
				isContentIdRemovalEnabled={isContentIdRemovalEnabled}
				publicLinkId={publicLinkId}
			/>
			<FabricContainer ref={refOfNodeToWatchForChange}>
				{refOfNodeToWatchForChange && queryHash ? (
					<DeepLinkHandler
						scrollTargetId={queryHash}
						refOfNodeToWatchForChange={refOfNodeToWatchForChange}
					/>
				) : null}
				<ScrollToHashLinkHandler anchorContainer={refOfNodeToWatchForChange} />
				<ContentRenderer contentId={contentId} scrollTo={queryHash} isMobileView={isMobileView} />
				<Box xcss={childrenContainerStyles}>{children}</Box>
				{isAnonymousUser && <LazyLearnMoreLozenge onClick={launchPLLearnMoreModal} />}
			</FabricContainer>
			<PageLoadEnd metric={PUBLIC_LINK_SHARED_PAGE_LOAD} />
		</Container>
	);
};
